import { useMemo, useState, useEffect } from "react";
import type { AdminCategory } from "@/hooks/useAdminCategories";
import type { AdminProduct } from "@/hooks/useAdminStore";
import { useAdminCategories } from "@/hooks/useAdminCategories";
import { useAdminMarketplaces } from "@/hooks/useAdminMarketplaces";
import {
  getMarketplaceFulfillmentDescription,
  getMarketplaceFulfillmentIcon,
  getMarketplaceFulfillmentLabel,
  isAmazonMarketplace,
} from "@/utils/marketplace";

const BADGES = ["", "Mais Vendido", "Top Vendas", "Oferta Relâmpago", "Melhor Custo-Benefício", "Exclusivo", "Amazon Choice", "Menor Preço", "Melhor Imagem", "Melhor para Games"];
const PRODUCT_DRAFT_STORAGE_KEY = "afiliabr_admin_product_draft_v1";

type ProductDraft = {
  form: Omit<AdminProduct, "id">;
  imageSecondary: string;
  imageTertiary: string;
  updatedAt: number;
};

interface Props {
  product?: AdminProduct | null;
  onSave: (data: Omit<AdminProduct, "id">) => void | Promise<void>;
  onClose: () => void;
  saving?: boolean;
}

function ImagePreviewCard({ image, label }: { image: string; label: string }) {
  return (
    <div className="mt-3 rounded-xl border border-gray-200 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      <div className="aspect-[4/3] w-full p-4 flex items-center justify-center overflow-hidden">
        {image ? (
          <img
            src={image}
            alt={label}
            className="max-w-full max-h-full w-full h-full object-contain"
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 text-gray-400 text-xs px-4 text-center">
            <i className="ri-image-line text-2xl"></i>
            <span>Prévia da imagem</span>
          </div>
        )}
      </div>
    </div>
  );
}

function ToggleField({ checked, onChange, label, icon, activeColor = "bg-orange-500", description }: { checked: boolean; onChange: (checked: boolean) => void; label: string; icon: string; activeColor?: string; description?: string }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer rounded-2xl border border-gray-200 px-4 py-3 bg-white">
      <div className="relative mt-0.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <div className={`w-11 h-6 rounded-full transition-colors ${checked ? activeColor : "bg-gray-200"}`}></div>
        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-5" : ""}`}></div>
      </div>
      <div>
        <span className="text-sm font-medium text-gray-700 inline-flex items-center gap-2">
          <i className={`${icon} text-base text-gray-500`}></i>
          {label}
        </span>
        {description && <p className="text-xs text-gray-500 mt-1">{description}</p>}
      </div>
    </label>
  );
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function getCategoryByName(categories: AdminCategory[], categoryName: string) {
  return categories.find((category) => category.name === categoryName) ?? null;
}

function createEmptyProduct(categories: AdminCategory[], marketplaceOptions: string[]): Omit<AdminProduct, "id"> {
  const firstCategory = categories[0] ?? null;

  return {
    name: "",
    slug: "",
    category: firstCategory?.name ?? "",
    categorySlug: firstCategory?.slug ?? "",
    subcategory: "",
    subcategorySlug: "",
    image: "",
    galleryImages: [],
    overviewText: "",
    price: 0,
    originalPrice: 0,
    discount: 0,
    rating: 4.5,
    reviews: 0,
    marketplace: marketplaceOptions[0] ?? "",
    affiliateLink: "",
    badge: null,
    freeShipping: true,
    fulfillment: false,
    officialStore: false,
    officialStoreName: "",
    showInDailyDeals: false,
    showInFeatured: false,
  };
}

function normalizeForm(form: Omit<AdminProduct, "id">, categories: AdminCategory[], marketplaceOptions: string[]) {
  const fallbackCategory = categories[0] ?? null;
  const selectedCategory = getCategoryByName(categories, form.category) ?? fallbackCategory;
  const subcategoryOptions = selectedCategory?.subcategories ?? [];
  const hasSubcategoryOptions = subcategoryOptions.length > 0;
  const normalizedSubcategory = hasSubcategoryOptions && subcategoryOptions.includes(form.subcategory)
    ? form.subcategory
    : "";
  const officialStoreName = String(form.officialStoreName ?? "").trim();

  return {
    ...form,
    category: selectedCategory?.name ?? "",
    categorySlug: selectedCategory?.slug ?? (selectedCategory ? slugify(selectedCategory.name) : ""),
    subcategory: normalizedSubcategory,
    subcategorySlug: normalizedSubcategory ? slugify(normalizedSubcategory) : "",
    marketplace: form.marketplace || marketplaceOptions[0] || "",
    freeShipping: Boolean(form.freeShipping),
    fulfillment: Boolean(form.fulfillment),
    officialStore: Boolean(officialStoreName),
    officialStoreName,
    showInDailyDeals: Boolean(form.showInDailyDeals),
    showInFeatured: Boolean(form.showInFeatured),
    badge: form.badge ?? null,
  };
}

function hasMeaningfulDraft(draft: ProductDraft) {
  return Boolean(
    draft.form.name.trim() ||
    draft.form.image.trim() ||
    draft.imageSecondary.trim() ||
    draft.imageTertiary.trim() ||
    draft.form.overviewText.trim() ||
    draft.form.affiliateLink.trim() ||
    draft.form.price > 0 ||
    draft.form.originalPrice > 0 ||
    draft.form.reviews > 0 ||
    draft.form.discount > 0 ||
    draft.form.badge ||
    draft.form.fulfillment ||
    draft.form.officialStoreName.trim() ||
    draft.form.showInDailyDeals ||
    draft.form.showInFeatured ||
    draft.form.freeShipping !== true
  );
}

function readStoredProductDraft(): ProductDraft | null {
  try {
    const raw = localStorage.getItem(PRODUCT_DRAFT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as ProductDraft;
    if (!parsed?.form || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasStoredProductDraft() {
  const draft = readStoredProductDraft();
  return Boolean(draft && hasMeaningfulDraft(draft));
}

export function clearStoredProductDraft() {
  try {
    localStorage.removeItem(PRODUCT_DRAFT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function writeStoredProductDraft(draft: ProductDraft) {
  try {
    if (!hasMeaningfulDraft(draft)) {
      clearStoredProductDraft();
      return;
    }

    localStorage.setItem(PRODUCT_DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // ignore
  }
}

export default function ProductModal({ product, onSave, onClose, saving = false }: Props) {
  const { categories } = useAdminCategories();
  const { activeMarketplaceOptions, activeOfficialStoreOptions } = useAdminMarketplaces();

  const categoryOptions = useMemo(() => categories.map((category) => category.name), [categories]);
  const hasMarketplaceOptions = activeMarketplaceOptions.length > 0;
  const hasCategoryOptions = categoryOptions.length > 0;
  const empty = useMemo(
    () => createEmptyProduct(categories, activeMarketplaceOptions),
    [activeMarketplaceOptions, categories]
  );

  const storedDraft = useMemo(() => (!product ? readStoredProductDraft() : null), [product]);
  const draftWasRestored = Boolean(!product && storedDraft && hasMeaningfulDraft(storedDraft));

  const [form, setForm] = useState<Omit<AdminProduct, "id">>(() => {
    if (product) {
      return normalizeForm({ ...product }, categories, activeMarketplaceOptions);
    }

    if (storedDraft && hasMeaningfulDraft(storedDraft)) {
      return normalizeForm(storedDraft.form, categories, activeMarketplaceOptions);
    }

    return empty;
  });
  const [imageSecondary, setImageSecondary] = useState(() => storedDraft?.imageSecondary ?? product?.galleryImages[0] ?? "");
  const [imageTertiary, setImageTertiary] = useState(() => storedDraft?.imageTertiary ?? product?.galleryImages[1] ?? "");

  const selectedCategory = useMemo(
    () => getCategoryByName(categories, form.category),
    [categories, form.category]
  );
  const subcategoryOptions = selectedCategory?.subcategories ?? [];
  const hasSubcategoryOptions = subcategoryOptions.length > 0;

  const fulfillmentLabel = getMarketplaceFulfillmentLabel(form.marketplace);
  const fulfillmentDescription = getMarketplaceFulfillmentDescription(form.marketplace);
  const fulfillmentIcon = getMarketplaceFulfillmentIcon(form.marketplace);
  const fulfillmentActiveColor = isAmazonMarketplace(form.marketplace) ? "bg-amber-500" : "bg-sky-500";

  useEffect(() => {
    if (product) {
      setForm(normalizeForm({ ...product }, categories, activeMarketplaceOptions));
      setImageSecondary(product.galleryImages[0] ?? "");
      setImageTertiary(product.galleryImages[1] ?? "");
      return;
    }

    setForm((prev) => normalizeForm(prev, categories, activeMarketplaceOptions));
  }, [activeMarketplaceOptions, categories, product]);

  useEffect(() => {
    if (product) return;

    writeStoredProductDraft({
      form,
      imageSecondary,
      imageTertiary,
      updatedAt: Date.now(),
    });
  }, [form, imageSecondary, imageTertiary, product]);

  const set = (field: keyof Omit<AdminProduct, "id">, value: unknown) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "name") {
        updated.slug = slugify(String(value));
      }

      if (field === "category") {
        const nextCategory = getCategoryByName(categories, String(value));
        const nextSubcategoryOptions = nextCategory?.subcategories ?? [];
        updated.category = nextCategory?.name ?? String(value);
        updated.categorySlug = nextCategory?.slug ?? slugify(String(value));
        updated.subcategory = "";
        updated.subcategorySlug = "";

        if (nextSubcategoryOptions.length === 0) {
          updated.subcategory = updated.category;
          updated.subcategorySlug = updated.categorySlug;
        }
      }

      if (field === "subcategory") {
        updated.subcategory = String(value);
        updated.subcategorySlug = slugify(String(value));
      }

      if (field === "officialStoreName") {
        updated.officialStoreName = String(value);
        updated.officialStore = Boolean(String(value).trim());
      }

      if (field === "price" || field === "originalPrice") {
        const price = field === "price" ? Number(value) : Number(updated.price);
        const originalPrice = field === "originalPrice" ? Number(value) : Number(updated.originalPrice);
        updated.discount = originalPrice > 0 && price < originalPrice ? Math.round(((originalPrice - price) / originalPrice) * 100) : 0;
      }

      return updated;
    });
  };

  const resetDraft = () => {
    clearStoredProductDraft();
    setForm(empty);
    setImageSecondary("");
    setImageTertiary("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasMarketplaceOptions || !hasCategoryOptions) return;

    const selectedCategoryData = getCategoryByName(categories, form.category);
    const hasLinkedSubcategories = (selectedCategoryData?.subcategories.length ?? 0) > 0;
    const resolvedCategorySlug = selectedCategoryData?.slug ?? slugify(form.category);
    const resolvedSubcategory = hasLinkedSubcategories ? form.subcategory.trim() : form.category;
    const resolvedSubcategorySlug = hasLinkedSubcategories ? slugify(resolvedSubcategory) : resolvedCategorySlug;
    const officialStoreName = form.officialStoreName.trim();

    const payload = {
      ...form,
      category: form.category,
      categorySlug: resolvedCategorySlug,
      subcategory: resolvedSubcategory,
      subcategorySlug: resolvedSubcategorySlug,
      officialStoreName,
      officialStore: Boolean(officialStoreName),
      galleryImages: [imageSecondary.trim(), imageTertiary.trim()].filter((image) => image && image !== form.image.trim()),
    };

    await onSave(payload);

    if (!product) {
      clearStoredProductDraft();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{product ? "Editar Produto" : "Novo Produto"}</h2>
            {!product && draftWasRestored && (
              <p className="text-xs text-orange-600 mt-1">Rascunho restaurado automaticamente para você continuar o cadastro.</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!product && hasStoredProductDraft() && (
              <button
                type="button"
                onClick={resetDraft}
                disabled={saving}
                className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                Limpar rascunho
              </button>
            )}
            <button onClick={onClose} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer disabled:opacity-50">
              <i className="ri-close-line text-gray-500"></i>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {(!hasMarketplaceOptions || !hasCategoryOptions) && (
            <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              {!hasMarketplaceOptions && <p>Cadastre ao menos 1 marketplace ativo antes de salvar produtos.</p>}
              {!hasCategoryOptions && <p>Cadastre ao menos 1 categoria antes de salvar produtos.</p>}
            </div>
          )}

          {!product && (
            <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
              O formulário salva seu progresso automaticamente enquanto você preenche. Você pode alternar de aba, copiar links e voltar depois sem perder o rascunho.
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Nome do Produto *</label>
            <input
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Ex: Samsung Smart TV 50&quot; Crystal UHD 4K"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">URL da Imagem Principal</label>
              <input
                value={form.image}
                onChange={(e) => set("image", e.target.value)}
                placeholder="https://..."
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
              <ImagePreviewCard image={form.image} label="Imagem principal" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">URL da Imagem 2</label>
              <input
                value={imageSecondary}
                onChange={(e) => setImageSecondary(e.target.value)}
                placeholder="https://..."
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
              <ImagePreviewCard image={imageSecondary} label="Imagem 2" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">URL da Imagem 3</label>
              <input
                value={imageTertiary}
                onChange={(e) => setImageTertiary(e.target.value)}
                placeholder="https://..."
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
              <ImagePreviewCard image={imageTertiary} label="Imagem 3" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Texto abaixo da imagem do produto</label>
            <textarea
              value={form.overviewText}
              onChange={(e) => set("overviewText", e.target.value)}
              placeholder="Digite aqui o texto de visão geral e orientações antes da compra."
              rows={6}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm resize-y focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Preço Atual (R$) *</label>
              <input
                required
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={(e) => set("price", parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Preço Original (R$)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.originalPrice}
                onChange={(e) => set("originalPrice", parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Desconto (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={form.discount}
                onChange={(e) => set("discount", parseInt(e.target.value) || 0)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 bg-gray-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Marketplace *</label>
              <select
                required
                disabled={!hasMarketplaceOptions}
                value={form.marketplace}
                onChange={(e) => set("marketplace", e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 cursor-pointer disabled:bg-gray-50 disabled:text-gray-400"
              >
                {!hasMarketplaceOptions && <option value="">Cadastre um marketplace</option>}
                {activeMarketplaceOptions.map((marketplace) => <option key={marketplace}>{marketplace}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Loja Oficial</label>
              <select
                value={form.officialStoreName}
                onChange={(e) => set("officialStoreName", e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 cursor-pointer"
              >
                <option value="">Sem loja oficial vinculada</option>
                {activeOfficialStoreOptions.map((officialStore) => <option key={officialStore} value={officialStore}>{officialStore}</option>)}
              </select>
              <p className="text-xs text-gray-500 mt-1">Cadastre opções em Marketplaces usando o tipo Loja Oficial.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Categoria *</label>
              <select
                required
                disabled={!hasCategoryOptions}
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 cursor-pointer disabled:bg-gray-50 disabled:text-gray-400"
              >
                {!hasCategoryOptions && <option value="">Cadastre uma categoria</option>}
                {categoryOptions.map((category) => <option key={category}>{category}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Subcategoria {hasSubcategoryOptions && "*"}</label>
              <select
                required={hasSubcategoryOptions}
                disabled={!hasCategoryOptions || !hasSubcategoryOptions}
                value={hasSubcategoryOptions ? form.subcategory : form.category}
                onChange={(e) => set("subcategory", e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 cursor-pointer disabled:bg-gray-50 disabled:text-gray-400"
              >
                {!hasCategoryOptions && <option value="">Selecione uma categoria</option>}
                {hasCategoryOptions && !hasSubcategoryOptions && <option value={form.category}>Sem subcategorias cadastradas</option>}
                {hasSubcategoryOptions && <option value="">Selecione a subcategoria</option>}
                {subcategoryOptions.map((subcategory) => <option key={subcategory} value={subcategory}>{subcategory}</option>)}
              </select>
              {hasCategoryOptions && !hasSubcategoryOptions && (
                <p className="text-xs text-amber-600 mt-1">Essa categoria ainda não possui subcategorias cadastradas. O produto será salvo usando a própria categoria como agrupamento.</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
              <i className="ri-links-line mr-1 text-orange-500"></i>
              Link de Afiliado *
            </label>
            <input
              required
              value={form.affiliateLink}
              onChange={(e) => set("affiliateLink", e.target.value)}
              placeholder="https://..."
              className="w-full px-4 py-2.5 rounded-xl border border-orange-200 bg-orange-50 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Badge</label>
              <select
                value={form.badge ?? ""}
                onChange={(e) => set("badge", e.target.value || null)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 cursor-pointer"
              >
                {BADGES.map((badge) => <option key={badge} value={badge}>{badge || "— Sem badge —"}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Avaliação (0-5)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={form.rating}
                onChange={(e) => set("rating", parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">Número Avaliações</label>
              <input
                type="number"
                step="1"
                min="0"
                value={form.reviews}
                onChange={(e) => set("reviews", parseInt(e.target.value, 10) || 0)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Exibir no site</p>
              <p className="text-xs text-gray-500 mt-1">Defina já no cadastro se este produto aparece em Ofertas do Dia e/ou Produtos em Destaque.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <ToggleField
                checked={form.showInDailyDeals}
                onChange={(checked) => set("showInDailyDeals", checked)}
                label="Ofertas do Dia"
                icon="ri-fire-line"
                activeColor="bg-red-500"
                description="Habilita o produto na vitrine de ofertas e no painel /admin/ofertas."
              />
              <ToggleField
                checked={form.showInFeatured}
                onChange={(checked) => set("showInFeatured", checked)}
                label="Produtos em Destaque"
                icon="ri-star-line"
                description="Habilita o produto na área de destaques da home e no painel /admin/destaques."
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ToggleField
              checked={form.freeShipping}
              onChange={(checked) => set("freeShipping", checked)}
              label="Frete Grátis"
              icon="ri-truck-line"
              description="Exibe o selo de frete grátis no card do produto."
            />
            <ToggleField
              checked={form.fulfillment}
              onChange={(checked) => set("fulfillment", checked)}
              label={fulfillmentLabel}
              icon={fulfillmentIcon}
              activeColor={fulfillmentActiveColor}
              description={fulfillmentDescription}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              Fechar
            </button>
            <button
              type="submit"
              disabled={saving || !hasMarketplaceOptions || !hasCategoryOptions || (hasSubcategoryOptions && !form.subcategory)}
              className="flex-1 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? "Salvando..." : product ? "Salvar Alterações" : "Adicionar Produto"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}